import { createFileRoute } from "@tanstack/react-router";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Loader2, RefreshCw, TrendingUpIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { useServerPagination } from "@/hooks/useServerPagination";
import { forecastApi, type Scenario } from "@/services/api/hr";

export const Route = createFileRoute("/portal/hr/forecast/")({
  component: ForecastPage,
});

type ScenarioSortKey =
  | "name"
  | "base_period"
  | "months_ahead"
  | "status"
  | "created_at_utc";

function formatAmount(value: string | undefined): string {
  // A missing period cell ("no data") is not the same as R0.
  if (value === undefined) return "—";
  const n = Number(value);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : "—";
}

const RUNNING_STATUSES = new Set(["running", "queued", "pending"]);

function ForecastPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const orgId = selectedOrganisation?.organisationId ?? "";
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const {
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    offset,
    searchTerm,
    setSearchTerm,
    getPaginationInfo,
  } = useServerPagination({ initialItemsPerPage: 25 });

  const [sortBy, setSortBy] = useState<ScenarioSortKey>("created_at_utc");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Reset page/search/selection at render time when the org changes.
  const [prevOrg, setPrevOrg] = useState(orgId);
  if (prevOrg !== orgId) {
    setPrevOrg(orgId);
    setCurrentPage(1);
    setSearchTerm("");
    setSelectedId(null);
  }

  const search = searchTerm.trim();

  const scenarios = useQuery({
    queryKey: ["hr-scenarios", orgId, offset, itemsPerPage, search, sortBy, sortOrder],
    queryFn: () =>
      forecastApi.listScenarios(token, {
        offset,
        limit: itemsPerPage,
        search,
        sort: sortBy,
        order: sortOrder,
      }),
    enabled: !!token && !!orgId,
    placeholderData: keepPreviousData,
    // Poll while any scenario on this page is still computing.
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((s) => RUNNING_STATUSES.has(s.status ?? ""))
        ? 4000
        : false,
  });

  const run = useMutation({
    mutationFn: (id: number) => forecastApi.runScenario(token, id),
    onSuccess: () => {
      toast.success("Forecast run started");
      queryClient.invalidateQueries({ queryKey: ["hr-scenarios"] });
      queryClient.invalidateQueries({ queryKey: ["hr-forecast-matrix"] });
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Run failed"),
  });

  const rows: Scenario[] = scenarios.data?.items ?? [];
  const total = scenarios.data?.total ?? 0;
  const { totalPages, startIndex, endIndex } = getPaginationInfo(total);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  const selectedRunning = rows.some(
    (s) => s.id === selectedId && RUNNING_STATUSES.has(s.status ?? ""),
  );

  // Clamp if the result set shrank under us.
  useEffect(() => {
    if (scenarios.data && totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [scenarios.data, totalPages, currentPage, setCurrentPage]);

  function handleSort(key: ScenarioSortKey) {
    if (sortBy === key) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(key);
      setSortOrder("asc");
    }
    setCurrentPage(1);
  }

  const getSortDirection = (key: ScenarioSortKey): "asc" | "desc" | null =>
    sortBy === key ? sortOrder : null;

  const onSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Forecasting</h1>
          <p className="mt-2 text-lg text-slate-600">
            Model payroll cost scenarios and view the projected spend matrix.
          </p>
        </div>
        <CreateScenarioDialog
          onCreated={() =>
            queryClient.invalidateQueries({ queryKey: ["hr-scenarios"] })
          }
        />
      </div>

      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => onSearch(e.target.value)}
            onClear={() => onSearch("")}
            placeholder="Search scenarios…"
            containerClassName="flex-1 min-w-[200px]"
            data-testid="scenario-search"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => scenarios.refetch()}
            disabled={scenarios.isFetching}
          >
            {scenarios.isFetching ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {scenarios.isError && (
          <p className="text-sm text-red-600">Failed to load scenarios.</p>
        )}

        {scenarios.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 && !scenarios.isError ? (
          <div className="text-center py-12">
            <TrendingUpIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {search ? "No scenarios match your search" : "No scenarios yet"}
            </h3>
            <p className="text-muted-foreground">
              {search
                ? "Try a different search term."
                : "Create one to start forecasting."}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  direction={getSortDirection("name")}
                  onSort={() => handleSort("name")}
                >
                  Name
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("base_period")}
                  onSort={() => handleSort("base_period")}
                >
                  Base period
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("months_ahead")}
                  onSort={() => handleSort("months_ahead")}
                >
                  Months
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("status")}
                  onSort={() => handleSort("status")}
                >
                  Status
                </SortableTableHead>
                <SortableTableHead direction={null} onSort={() => {}} disabled className="text-right">
                  Actions
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} data-testid={`scenario-${s.id}`}>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell>{s.base_period}</TableCell>
                  <TableCell>{s.months_ahead}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {s.status ?? "—"}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => run.mutate(s.id)}
                      disabled={run.isPending}
                      data-testid={`run-scenario-${s.id}`}
                    >
                      Run
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedId(s.id)}
                      data-testid={`view-scenario-${s.id}`}
                    >
                      View forecast
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {total > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={currentPage}
              totalPages={totalPages}
              totalCount={total}
              pageSize={itemsPerPage}
              pageSizeOptions={[10, 25, 50]}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={setCurrentPage}
              onPageSizeChange={setItemsPerPage}
              onFirstPage={() => setCurrentPage(1)}
              onLastPage={() => setCurrentPage(totalPages)}
              onPrevPage={() => setCurrentPage(currentPage - 1)}
              onNextPage={() => setCurrentPage(currentPage + 1)}
            />
          </div>
        )}
      </div>

      {selectedId !== null && (
        <ForecastMatrixCard
          token={token}
          orgId={orgId}
          scenarioId={selectedId}
          isRunning={selectedRunning}
        />
      )}
    </div>
  );
}

function ForecastMatrixCard({
  token,
  orgId,
  scenarioId,
  isRunning,
}: {
  token: string | null;
  orgId: string;
  scenarioId: number;
  isRunning: boolean;
}) {
  const matrix = useQuery({
    queryKey: ["hr-forecast-matrix", orgId, scenarioId],
    queryFn: () => forecastApi.matrix(token, scenarioId),
    enabled: !!token && !!orgId,
    refetchInterval: isRunning ? 4000 : false,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Forecast matrix</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {matrix.isLoading && (
          <p className="text-sm text-muted-foreground">Loading forecast…</p>
        )}
        {matrix.isError && (
          <p className="text-sm text-red-600">Failed to load the forecast matrix.</p>
        )}
        {matrix.data && matrix.data.rows.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No forecast yet — run this scenario first.
          </p>
        )}
        {matrix.data && matrix.data.rows.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Payroll item</TableHead>
                {matrix.data.periods.map((p) => (
                  <TableHead key={p} className="text-right">
                    {p}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {matrix.data.rows.map((row, idx) => (
                <TableRow
                  key={`${row.payroll_item_id ?? "sub"}-${idx}`}
                  className={row.is_subtotal ? "font-semibold" : undefined}
                  data-testid={`matrix-row-${idx}`}
                >
                  <TableCell>{row.payroll_item_name}</TableCell>
                  {matrix.data!.periods.map((p) => (
                    <TableCell key={p} className="text-right tabular-nums">
                      {formatAmount(row.amounts[p])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow className="border-t-2 font-semibold">
                <TableCell>Grand total</TableCell>
                {matrix.data.periods.map((p) => (
                  <TableCell key={p} className="text-right tabular-nums">
                    {formatAmount(matrix.data!.grand_total[p])}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function CreateScenarioDialog({ onCreated }: { onCreated: () => void }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [basePeriod, setBasePeriod] = useState("");
  const [monthsAhead, setMonthsAhead] = useState("12");

  const create = useMutation({
    mutationFn: () =>
      forecastApi.createScenario(token, {
        name: name.trim(),
        base_period: basePeriod,
        months_ahead: Number(monthsAhead),
      }),
    onSuccess: () => {
      toast.success("Scenario created");
      setOpen(false);
      setName("");
      setBasePeriod("");
      setMonthsAhead("12");
      onCreated();
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Create failed"),
  });

  const monthsNum = Number(monthsAhead);
  const canSubmit =
    name.trim().length > 0 &&
    basePeriod.length > 0 &&
    Number.isInteger(monthsNum) &&
    monthsNum >= 1 &&
    monthsNum <= 120;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="add-scenario">New scenario</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New forecast scenario</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="scenario-name">Name</Label>
            <Input
              id="scenario-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. FY27 baseline"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="scenario-base">Base period</Label>
              <Input
                id="scenario-base"
                type="date"
                value={basePeriod}
                onChange={(e) => setBasePeriod(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="scenario-months">Months ahead</Label>
              <Input
                id="scenario-months"
                type="number"
                min="1"
                max="120"
                value={monthsAhead}
                onChange={(e) => setMonthsAhead(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={() => create.mutate()}
            disabled={!canSubmit || create.isPending}
            data-testid="create-scenario"
          >
            {create.isPending ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
